import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { always, applySpec } from 'ramda';

import { GenericPayloadType, GenericStateType } from '@ezugi/types';

import { hasKey } from '../../../../types/src/utils';
import {
  LOW_BALANCE_DIALOG,
  DISCONNECTED_SESSION_DIALOG,
  DISCONNECTED_FOR_INACTIVITY_DIALOG,
  TABLE_CLOSED_DIALOG,
  DIALOG_PRIORITIES,
} from '../constants/dialog';

const errorDialogs = [
  LOW_BALANCE_DIALOG,
  DISCONNECTED_SESSION_DIALOG,
  DISCONNECTED_FOR_INACTIVITY_DIALOG,
  TABLE_CLOSED_DIALOG,
];

const getSoundName = ({ name }: {name?: string} = {}) => (name && errorDialogs.includes(name) ? 'error' : null);

type DialogAddPayload = { name: keyof typeof DIALOG_PRIORITIES
  | 'SidebetsInfoDialog'| 'SixCardDialog' } & GenericPayloadType;

type GenericDialogButton = {
  action: string;
  props: {
    label: string;
    accent?: boolean;
  }
}

type DialogItem = {
  name?: string;
  type?: string;
  data?: GenericStateType;
  html?: string;
  title?: string;
  message?: string;
  className?: string;
  description?: string;
  closeTimer?: number | null;
  buttons?: GenericDialogButton[];
  priority: number;
};

export type DialogQueueState = {
  queue: DialogItem[];
  currentDialog: DialogItem | null;
}

const initialState: DialogQueueState = {
  queue: [],
  currentDialog: null,
};

const dialogSlice = createSlice({
  name: 'dialog',
  initialState,
  reducers: {
    add: {
      reducer(state, { payload }: PayloadAction<DialogAddPayload>) {
        const dialogItem: DialogItem = {
          ...payload,
          priority: hasKey(DIALOG_PRIORITIES, payload.name) ? DIALOG_PRIORITIES[payload.name] : 3,
        };

        if (!state.currentDialog) {
          state.currentDialog = dialogItem;
        } else {
          state.queue.push(dialogItem);
        }
      },
      prepare(payload: DialogAddPayload) {
        const meta = applySpec({
          spriteName: always('base_sprite'),
          soundName: getSoundName,
        })(payload);

        return { payload, meta };
      },
    },
    remove(state) {
      state.currentDialog = null;
    },
    showNext(state) {
      if (state.queue.length > 0) {
        state.queue.sort((a, b) => a.priority - b.priority);
        const nextDialog = state.queue.shift();
        if (nextDialog) {
          state.currentDialog = nextDialog;
        }
      }
    },
  },
});

export const dialogActions = dialogSlice.actions;
export default dialogSlice.reducer;
